import { MultiSelect as MantineMultiSelect } from '@mantine/core'
import { FormikProvider } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import styles from '../../shared/MultiSelectStyles.module.scss'
import { skipClientSideFiltering } from '../../shared/accountSelectOptions'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import {
  AcknowledgeModal,
  ActionModal,
  AsyncSelect,
  Flex,
  FormField,
  Modal,
  MultiSelect,
  Text,
  TextInput
} from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useAssignmentRuleForm } from './hooks/useAssignmentRuleForm'
import { useProgramSelection } from './hooks/useProgramSelection'

type FieldError = string | string[] | undefined

const getFieldError = (error: FieldError): string | undefined => {
  if (typeof error === 'string') return error

  if (Array.isArray(error)) return error.join(', ')

  return undefined
}

const getFieldStatus = (isTouched: boolean | undefined, error: FieldError) =>
  isTouched && error ? 'danger' : 'default'

const getTouchedFieldError = (isTouched: boolean | undefined, error: FieldError): string | undefined =>
  isTouched ? getFieldError(error) : undefined

export type GrantsAssignmentRulesFormProps = Readonly<{
  programs?: { label: string; value: string }[]
  selectedProgramId?: string
  onProgramChange?: (programId: string | undefined) => void
  onProgramSelect?: (programId: string) => void
  programSearchQuery?: string
  onProgramSearchChange: (query: string) => void
  programsLoading?: boolean
  taskTemplates: { label: string; value: string }[]
  accounts: { label: string; value: string }[]
  onAccountSearchChange: (query: string) => void
  accountsLoading?: boolean
  nominatorGroups: { label: string; value: string }[]
  nominatorGroupsLoading?: boolean
  countries: { label: string; value: string }[]
  invalidateQuery?: () => void
  open: boolean
  onClose: () => void
}>

export function AddAssignmentRulesForm({
  programs,
  selectedProgramId,
  onProgramChange,
  onProgramSelect,
  programSearchQuery = '',
  onProgramSearchChange,
  programsLoading = false,
  taskTemplates,
  countries,
  accounts,
  onAccountSearchChange,
  accountsLoading = false,
  nominatorGroups,
  nominatorGroupsLoading = false,
  invalidateQuery,
  open,
  onClose
}: GrantsAssignmentRulesFormProps) {
  const { grantProgramService } = useServices()
  const { t } = useTranslation()

  const [dialogState, setDialogState] = useState<'success' | 'error' | undefined>(undefined)

  const [{ isPending: submitPostPending }, { apiFunc: post }] = useMutation(
    grantProgramService.addAutoAssignmentRule,
    () => setDialogState('success'),
    () => setDialogState('error')
  )

  const { form, resetDependentFields } = useAssignmentRuleForm({
    allCountries: countries,
    onSubmit: post
  })

  const { errors, values, touched, handleChange, setFieldValue, handleBlur, handleSubmit } = form

  const { selectedProgramOption, programsWithSelected } = useProgramSelection(
    programs,
    selectedProgramId,
    values.selectedProgramId,
    setFieldValue
  )

  useEffect(() => {
    if (!open) {
      setDialogState(undefined)
    }
  }, [open])

  const handleProgramChange = (event: { label: string; value: string } | null) => {
    const programId = event?.value
    const previousProgramId = values.selectedProgramId

    if (programId) {
      if (previousProgramId !== programId) {
        resetDependentFields()
      }

      setFieldValue('selectedProgramId', programId)
      onProgramChange?.(programId)
      onProgramSelect?.(programId)
    } else {
      setFieldValue('selectedProgramId', '')
      resetDependentFields()
      onProgramChange?.(undefined)
    }
  }

  const handleCloseSuccessModal = () => {
    form.resetForm()
    setDialogState(undefined)
    invalidateQuery?.()
    onClose()
  }

  const handleCancel = () => {
    form.resetForm()
    onClose()
  }

  const selectedCountries = useMemo(
    () => countries.filter(c => values.countries.includes(c.value)),
    [countries, values.countries]
  )

  const successModal = dialogState === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('tasksAutoAssignmentRule.dialog.success.title')}
      buttonText={t('button.close')}
      handleClose={handleCloseSuccessModal}
      viewTestId="task-auto-assignment-rule-success-modal"
      buttonTestId="close-task-auto-assignment-rule-success-modal"
    >
      <Text size="small">{t('tasksAutoAssignmentRule.dialog.success.addedRule')}</Text>
    </AcknowledgeModal>
  )

  const errorModal = dialogState === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('tasksAutoAssignmentRule.dialog.error.title')}
      description={t('tasksAutoAssignmentRule.dialog.error.description')}
      buttonText={t('button.close')}
      handleClose={() => setDialogState(undefined)}
      viewTestId="task-auto-assignment-rule-error-modal"
      buttonTestId="close-task-auto-assignment-rule-error-modal"
    />
  )

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="grants-assignment-rules-form-modal">
      {successModal || errorModal || (
        <ActionModal
          title={t('tasksAutoAssignmentRule.form.title.new')}
          primaryButtonText={t('button.save')}
          secondaryButtonText={t('button.cancel')}
          handleClose={handleCancel}
          handleSubmit={handleSubmit}
          loading={submitPostPending}
          disabled={submitPostPending || !form.isValid || !values.selectedProgramId}
          primaryBtnTestId="save-assignment-rule-button"
          secondaryBtnTestId="cancel-assignment-rule-button"
        >
          <form onSubmit={handleSubmit}>
            <FormikProvider value={form}>
              <Flex direction="column" gap="lg">
                <FormField
                  label={t('tasksAutoAssignmentRule.form.name.label')}
                  status={getFieldStatus(touched.name, errors.name)}
                  statusMessage={errors.name}
                  data-testid="ruleName"
                  necessity="required"
                >
                  <TextInput
                    name="name"
                    value={values.name}
                    placeholder={t('tasksAutoAssignmentRule.form.name.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </FormField>
                <FormField
                  label={t('tasksAutoAssignmentRule.form.program.label')}
                  data-testid="programSelect"
                  necessity="required"
                  status={getFieldStatus(touched.selectedProgramId, errors.selectedProgramId)}
                  statusMessage={getTouchedFieldError(touched.selectedProgramId, errors.selectedProgramId)}
                >
                  <AsyncSelect
                    key={values.selectedProgramId || 'no-program'}
                    name="selectedProgramId"
                    placeholder={t('tasksAutoAssignmentRule.form.program.placeholder')}
                    options={programsWithSelected ?? []}
                    defaultValue={selectedProgramOption}
                    onChange={handleProgramChange}
                    query={programSearchQuery}
                    setQuery={onProgramSearchChange}
                    loading={programsLoading}
                    loadingText={t('typography.searching')}
                    noResultsFoundText={t('typography.noResultsFound')}
                    data-testid="programSelect"
                  />
                </FormField>
                <FormField
                  label={t('tasksAutoAssignmentRule.form.taskTemplateId.label')}
                  data-testid="taskTemplateIds"
                  status={getFieldStatus(touched.taskTemplateIds, errors.taskTemplateIds)}
                  statusMessage={getFieldError(errors.taskTemplateIds)}
                  necessity="required"
                >
                  <MultiSelect
                    key={`taskTemplates-${values.selectedProgramId}`}
                    placeholder={t('tasksAutoAssignmentRule.form.taskTemplateId.placeholder')}
                    defaultValue={taskTemplates.filter(tt => values.taskTemplateIds.includes(tt.value))}
                    options={taskTemplates ?? []}
                    disabled={!values.selectedProgramId}
                    onChange={(selectedOptions: Array<{ label: string; value: string }>) => {
                      setFieldValue(
                        'taskTemplateIds',
                        selectedOptions.map(o => o.value)
                      )
                    }}
                  />
                </FormField>
                <FormField
                  label={t('tasksAutoAssignmentRule.form.nominatorGroups.label')}
                  data-testid="nominatorGroups"
                  status={getFieldStatus(touched.nominatorGroups, errors.nominatorGroups)}
                  statusMessage={getFieldError(errors.nominatorGroups)}
                  necessity="optional"
                >
                  <MultiSelect
                    key={`nominatorGroups-${values.selectedProgramId}`}
                    placeholder={t('tasksAutoAssignmentRule.form.nominatorGroups.placeholder')}
                    defaultValue={nominatorGroups.filter(ng => values.nominatorGroups.includes(ng.value))}
                    options={nominatorGroups}
                    disabled={!values.selectedProgramId || nominatorGroupsLoading}
                    onChange={(selectedOptions: Array<{ label: string; value: string }>) => {
                      setFieldValue(
                        'nominatorGroups',
                        selectedOptions.map(o => o.value)
                      )
                    }}
                  />
                </FormField>
                <FormField
                  label={t('tasksAutoAssignmentRule.form.countries.label')}
                  data-testid="countries"
                  status={getFieldStatus(touched.countries, errors.countries)}
                  statusMessage={getFieldError(errors.countries)}
                  necessity="optional"
                >
                  <MultiSelect
                    placeholder={t('tasksAutoAssignmentRule.form.countries.placeholder')}
                    options={countries ?? []}
                    defaultValue={selectedCountries}
                    onChange={(selectedOptions: Array<{ label: string; value: string }>) => {
                      setFieldValue(
                        'countries',
                        selectedOptions.map(o => o.value)
                      )
                    }}
                  />
                </FormField>
                <FormField
                  label={t('tasksAutoAssignmentRule.form.accounts.label')}
                  data-testid="accountIds"
                  status={getFieldStatus(touched.accountIds, errors.accountIds)}
                  statusMessage={getFieldError(errors.accountIds)}
                  necessity="optional"
                >
                  <MantineMultiSelect
                    placeholder={t('tasksAutoAssignmentRule.form.accounts.placeholder')}
                    data={accounts ?? []}
                    value={values.accountIds}
                    disabled={!values.selectedProgramId || accountsLoading}
                    onChange={selectedValues => {
                      setFieldValue('accountIds', selectedValues)
                    }}
                    searchable
                    filter={skipClientSideFiltering}
                    onSearchChange={searchValue => {
                      onAccountSearchChange(searchValue)
                    }}
                    nothingFoundMessage={t('typography.noResultsFound')}
                    comboboxProps={{ withinPortal: false }}
                    data-testid="accountSelect"
                    className={styles.customMultiSelect}
                  />
                </FormField>
              </Flex>
            </FormikProvider>
          </form>
        </ActionModal>
      )}
    </Modal>
  )
}
